import type { ValueOf } from "ts-essentials";

import { QUEUE_SYMBOLS } from "@/game-lol/constants/constants.mjs";
import * as ARAM from "@/inline-assets/hextech-map-aram.svg";
import * as SR from "@/inline-assets/hextech-map-sr.svg";
import * as Flex from "@/inline-assets/hextech-queue-flex.svg";
import * as RGM from "@/inline-assets/hextech-queue-rgm.svg";
import * as SoloDuo from "@/inline-assets/hextech-queue-solo.svg";
import SymbolMap from "@/util/symbol-map.mjs";

export const QUEUE_SYMBOL_TO_OBJECT = new SymbolMap({
  [QUEUE_SYMBOLS.rankedSoloDuo]: {
    gql: "RANKED_SOLO_5X5",
    id: 420,
    key: ["4", "420"],
    dashed: "ranked-solo-duo",
    hideRole: false,
    label: ["lol:queueTypes.ranked_solo", "Ranked Solo"],
    icon: SoloDuo,
  },
  [QUEUE_SYMBOLS.rankedFlex]: {
    gql: "RANKED_FLEX_SR",
    id: 440,
    key: ["440"],
    dashed: "ranked-flex",
    hideRole: false,
    label: ["lol:queueTypes.ranked_flex", "Ranked Flex"],
    icon: Flex,
  },
  [QUEUE_SYMBOLS.clash]: {
    gql: "CLASH_SR",
    id: 700,
    key: ["700", "701"],
    dashed: "clash",
    hideRank: true,
    hideRole: false,
    hideFilter: true,
    label: ["lol:queueTypes.clash", "Clash"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.clashAram]: {
    gql: "CLASH_ARAM",
    id: 720,
    key: ["720", "721"],
    dashed: "clash-aram",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.clash_aram", "ARAM Clash"],
    icon: ARAM,
  },
  [QUEUE_SYMBOLS.clashUrf]: {
    gql: "SUMMONERS_RIFT_URF",
    id: 740,
    key: ["740", "741"],
    dataKey: "SUMMONERS_RIFT_URF",
    dashed: "urf",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.clash_urf", "URF Clash"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.normalDraft]: {
    gql: "SUMMONERS_RIFT_DRAFT_PICK",
    id: 400,
    key: ["14", "17", "400"],
    dashed: "normal-draft",
    hideRank: true,
    hideRole: false,
    label: ["lol:queueTypes.normal_draft", "Normal Draft"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.normalBlind]: {
    gql: "SUMMONERS_RIFT_BLIND_PICK",
    id: 430,
    key: ["16", "430"],
    dashed: "normal-blind",
    hideRank: true,
    hideRole: false,
    label: ["lol:queueTypes.normal_blind", "Normal Blind"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.ultimateSpellbook]: {
    gql: "ULTIMATE_SPELLBOOK",
    id: 1400,
    key: ["1400"],
    dashed: "ultimate-spellbook",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.ultimateSpellbook", "Ultimate Spellbook"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.nexusBlitz]: {
    gql: "NEXUS_BLITZ",
    id: 1300,
    key: ["1200", "1300"],
    dashed: "nexus-blitz",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.nexus_blitz", "Nexus Blitz"],
    icon: RGM,
    comingSoon: false,
  },
  [QUEUE_SYMBOLS.urf]: {
    gql: "SUMMONERS_RIFT_URF",
    id: 900,
    key: ["318", "900", "1010", "1900"],
    dashed: "urf",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.urf", "URF"],
    icon: RGM,
    // comingSoon: true,
  },
  [QUEUE_SYMBOLS.arena]: {
    gql: "CHERRY",
    id: 1700,
    key: ["1700", "1710"],
    dashed: "arena",
    hideRole: true,
    hideFilter: true,
    label: ["lol:arena", "Arena"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.brawl]: {
    gql: "BRAWL",
    id: 2300,
    key: ["2300"],
    dashed: "brawl",
    hideRole: true,
    hideFilter: true,
    label: ["lol:queueTypes.brawl", "Brawl"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.swarm]: {
    gql: "STRAWBERRY",
    id: 1810,
    key: ["1810", "1820", "1830", "1840"],
    dashed: "swarm",
    hideRole: true,
    hideFilter: true,
    label: ["lol:swarm", "Swarm"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.oneForAll]: {
    gql: "ONE_FOR_ALL", // fetch aram builds
    id: 1020,
    key: ["1020"],
    dashed: "one-for-all",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.oneForAll", "One For All"],
    icon: RGM,
  },
  [QUEUE_SYMBOLS.aram]: {
    gql: "HOWLING_ABYSS_ARAM",
    id: 450,
    key: ["65", "450", "920"],
    dashed: "aram",
    hideRole: true,
    hideRank: true,
    label: ["lol:queueTypes.aram", "ARAM"],
    icon: ARAM,
  },
  [QUEUE_SYMBOLS.quickplay]: {
    gql: "QUICKPLAY",
    id: 490,
    key: ["490"],
    dashed: "quickplay",
    hideRole: false,
    hideRank: true,
    label: ["lol:queueTypes.quickplay", "Quickplay"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.swiftplay]: {
    gql: "SWIFTPLAY",
    id: 480,
    key: ["480"],
    dashed: "swiftplay",
    hideRole: false,
    hideRank: true,
    label: ["lol:queueTypes.swiftplay", "Swiftplay"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.coopVsAiIntro]: {
    gql: "CO_OP_VS_AI_INTRO_BOT",
    id: 810,
    key: ["31", "810", "830", "870"],
    dashed: "coop-vs-ai-intro",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.co_op_vs_ai_intro_bot", "Co-op vs AI Intro Bot"],
    icon: SR,
  },
  [QUEUE_SYMBOLS.coopVsAiBeginner]: {
    gql: "CO_OP_VS_AI_BEGINNER_BOT",
    id: 820,
    key: ["32", "820", "840", "880"],
    dashed: "co-op-vs-ai-beginner-bot",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: [
      "lol:queueTypes.co_op_vs_ai_beginner_bot",
      "Co-op vs AI Beginner Bot",
    ],
    icon: SR,
  },
  [QUEUE_SYMBOLS.coopVsAiIntermediate]: {
    gql: "CO_OP_VS_AI_INTERMEDIATE_BOT",
    id: 800,
    key: ["33", "800", "850", "890"],
    dashed: "co-op-vs-ai-intermediate-bot",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: [
      "lol:queueTypes.co_op_vs_ai_intermediate_bot",
      "Co-op vs AI Intermediate Bot",
    ],
    icon: SR,
  },
  [QUEUE_SYMBOLS.custom]: {
    gql: "CUSTOM_GAME", // custom games aren't supported by BE
    id: 0,
    key: ["0", "-1"],
    dashed: "custom",
    hideRole: true,
    hideRank: true,
    hideFilter: true,
    label: ["lol:queueTypes.custom", "Custom"],
    icon: null,
  },
});

const symbolMap: Record<string, symbol> = {
  // Some obscure internal rito strings...
  aram_unranked_5x5: QUEUE_SYMBOLS.aram,
  cherry: QUEUE_SYMBOLS.arena,
};

for (const symbol of Object.getOwnPropertySymbols(QUEUE_SYMBOL_TO_OBJECT)) {
  for (const k of ["gql", "key", "dashed", "id"]) {
    const v = QUEUE_SYMBOL_TO_OBJECT[symbol][k] || "";
    if (Array.isArray(v)) {
      for (const entry of v) {
        symbolMap[`${entry}`.toLowerCase()] = symbol;
      }
    } else {
      symbolMap[`${v}`.toLowerCase()] = symbol;
    }
  }
}

/**
 * Cast strings to symbols, used for data model transformation.
 */
export default function QueueSymbol(value: string | symbol | number): symbol {
  if (typeof value === "symbol") return value;
  return symbolMap[`${value}`.toLowerCase()];
}

export const getQueueDetails = (
  queue: string | symbol,
): ValueOf<typeof QUEUE_SYMBOL_TO_OBJECT> => {
  const res = QUEUE_SYMBOL_TO_OBJECT[QueueSymbol(queue)];

  if (!res && queue) {
    return QUEUE_SYMBOL_TO_OBJECT[QUEUE_SYMBOLS.custom];
  }

  return res;
};

interface FilterQueueOptions {
  filterFn: (
    value: keyof typeof QUEUE_SYMBOL_TO_OBJECT,
    index: number,
  ) => boolean;
}

export const getQueueFilters = (t: unknown, options?: FilterQueueOptions) => {
  const properties = Object.getOwnPropertySymbols(QUEUE_SYMBOL_TO_OBJECT);
  const filter = options?.filterFn
    ? properties.filter(options.filterFn)
    : properties;

  return filter.map((key) => {
    const { gql, label } = QUEUE_SYMBOL_TO_OBJECT[key];

    return {
      value: gql,
      text: label,
    };
  });
};
